import { Component, computed, effect, input } from '@angular/core'
import { FormControl, ReactiveFormsModule } from '@angular/forms'
import { EmailInputComponent } from '@app/v2/shared/components/inputs/email-input/email-input.component'
import { SelectInputComponent } from '@app/v2/shared/components/inputs/select-input/select-input.component'
import { MobileCollapsibleSectionComponent } from '@app/v2/shared/components/mobile-collapsible-section/mobile-collapsible-section.component'
import { selfPrintingPersonaSettings } from '@app/v2/event/types/settings.type'
import { selfPrintingPersonaSettingsType } from '@app/v2/event/enums/settings.enum'

type PersonaSetting = selfPrintingPersonaSettings[number]
type PersonaId = number | string
type InteractOption = { id: PersonaId; name: string }
type FormValues = { interactions: string }

@Component({
  selector: 'app-handshake-persona-card',
  standalone: true,
  imports: [ReactiveFormsModule, SelectInputComponent, MobileCollapsibleSectionComponent],
  templateUrl: './handshake-persona-card.component.html',
  styleUrl: './handshake-persona-card.component.scss'
})
export class HandshakePersonaCardComponent {
  public readonly persona = input.required<PersonaSetting>()
  public readonly allPersonas = input.required<selfPrintingPersonaSettings>()
  public readonly initialExpanded = input<boolean>(false)

  public readonly interactsWithControl = new FormControl<PersonaId[]>([], { nonNullable: true })

  public readonly interactOptions = computed<InteractOption[]>(() => {
    return this.allPersonas()
      .filter((persona): persona is PersonaSetting & { id: PersonaId; name: string } => persona.id != null && !!persona.name)
      .map((persona) => ({ id: persona.id, name: persona.name.toUpperCase() }))
  })

  private readonly _personaMaps = computed(() => {
    const nameToId = new Map<string, PersonaId>()
    const idToName = new Map<string, string>()
    const slugToId = new Map<string, PersonaId>()
    const idToSlug = new Map<string, string>()
    for (const persona of this.allPersonas()) {
      if (persona.id != null && persona.name) {
        nameToId.set(persona.name, persona.id)
        idToName.set(String(persona.id), persona.name)
      }
      if (persona.id != null && persona.slug) {
        slugToId.set(persona.slug, persona.id)
        idToSlug.set(String(persona.id), persona.slug)
      }
    }
    return { nameToId, idToName, slugToId, idToSlug }
  })

  private _savedValues: FormValues = { interactions: '' }
  private _loadedPersonaId: PersonaId | null | undefined = undefined

  constructor() {
    effect(() => this._fillFormControlsFromPersona())
  }

  /**
   * Gets the persona name in uppercase.
   * @returns {string} - The persona name in uppercase.
   */
  get personaName(): string {
    return this.persona().name?.toUpperCase()
  }

  /**
   * Fills the form controls from the persona's saved handshake settings.
   * Skips when the same persona id has already been filled, so that
   * unsaved user edits aren't wiped by parent re-emissions.
   * @returns {void} - Fills the form controls from the persona's saved handshake settings.
   */
  private _fillFormControlsFromPersona(): void {
    if (!this.persona()) return
    if (this.persona().id === this._loadedPersonaId) return
    this._loadedPersonaId = this.persona().id ?? null

    const { slugToId, nameToId } = this._personaMaps()
    const savedCoordinators = this.persona().handshakes?.coordinators ?? []
    const savedInteractions = this.persona().handshakes?.interactions ?? []
    const savedInteractionIds = savedInteractions.map((value) => slugToId.get(value) ?? nameToId.get(value)).filter((id): id is PersonaId => id != null)
    this.interactsWithControl.setValue(savedInteractionIds, { emitEvent: false })
    this._savedValues = this._getCurrentValues()
  }

  /**
   * Builds a payload entry for the save endpoint, or null when nothing changed.
   * @returns {PersonaSetting | null} - The payload entry for the save endpoint, or null when nothing changed.
   */
  public buildPayloadEntry(): PersonaSetting | null {
    if (!this.persona().id) return null

    const current = this._getCurrentValues()
    if (current.interactions === this._savedValues.interactions) return null

    const { idToSlug } = this._personaMaps()
    const interactions = this._selectedInteractionIds()
      .map((id) => idToSlug.get(String(id)))
      .filter((slug): slug is string => !!slug)

    return {
      id: this.persona().id,
      handshakes: { interactions },
      type: selfPrintingPersonaSettingsType.HANDSHAKE
    }
  }

  /**
   * Sorts the interactions by slug to ensure consistent payloads.
   * @returns {FormValues} - The current values of the form controls.
   */
  private _getCurrentValues(): FormValues {
    const { idToSlug } = this._personaMaps()
    const interactions = this._selectedInteractionIds()
      .map((id) => idToSlug.get(String(id)))
      .filter((slug): slug is string => !!slug)
      .sort()
      .join(',')
    return { interactions }
  }

  /**
   * `select-input` may set a single id or an array depending on selection count / version.
   * @returns Selected persona ids as an array.
   */
  private _selectedInteractionIds(): PersonaId[] {
    const value = this.interactsWithControl.value
    if (!value) return []
    return Array.isArray(value) ? value : [value]
  }
}
